import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import { useTokenData } from "@/context/TokenDataContext";

export const usePortfolioCelebration = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.12 });
  const { marketCap, isUnlocked, isSimulated, unlockMarketCap } = useTokenData();
  const [reduced, setReduced] = useState(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  const [visible, setVisible] = useState(() => !document.hidden);
  const [blocked, setBlocked] = useState(false);
  const [pending, setPending] = useState(null);
  const [phase, setPhase] = useState("idle");
  const [count, setCount] = useState(0);
  const previous = useRef(null);
  const consumed = useRef(new Set());
  const timers = useRef([]);
  const stop = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  const remember = (key) => {
    consumed.current.add(key);
    try { sessionStorage.setItem(key, "1"); } catch { /* Optional storage; keep mounted-memory guard. */ }
  };

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onMotion = () => setReduced(media.matches);
    const onVisibility = () => setVisible(!document.hidden);
    const checkOverlay = () => setBlocked(Boolean(document.querySelector('[data-testid="unlock-celebration"]')));
    media.addEventListener("change", onMotion);
    document.addEventListener("visibilitychange", onVisibility);
    // Existing global celebration is untouched; wait until it clears instead of stacking effects.
    const observer = new MutationObserver(checkOverlay);
    const root = document.querySelector(".kb-root");
    if (root) observer.observe(root, { childList: true });
    checkOverlay();
    return () => {
      media.removeEventListener("change", onMotion);
      document.removeEventListener("visibilitychange", onVisibility);
      observer.disconnect();
      stop();
    };
  }, []);

  useEffect(() => {
    if (!Number.isFinite(marketCap)) return;
    const before = previous.current;
    previous.current = isUnlocked;
    if (!isUnlocked) { setPending(null); setPhase("idle"); stop(); return; }
    if (before !== false) return;
    const key = `kingbullish:portfolio-rainbow:v1:${unlockMarketCap}:${isSimulated ? "simulation" : "live"}`;
    let used = consumed.current.has(key);
    try { used ||= sessionStorage.getItem(key) === "1"; } catch { /* Optional storage. */ }
    if (used) return;
    if (reduced) { remember(key); return; }
    setPending(key);
  }, [marketCap, isUnlocked, isSimulated, unlockMarketCap, reduced]);

  useEffect(() => {
    if (!pending || !isUnlocked || !inView || !visible || blocked || reduced) return;
    // Give the existing app-wide overlay time to mount after the same market-cap update.
    const launch = setTimeout(() => {
      if (document.querySelector('[data-testid="unlock-celebration"]')) { setBlocked(true); return; }
      remember(pending);
      setPending(null);
      setCount((value) => value + 1);
      setPhase("charging");
      stop();
      timers.current = [
        setTimeout(() => setPhase("burst"), 1000),
        setTimeout(() => setPhase("reveal"), 1650),
        setTimeout(() => setPhase("idle"), 2800),
      ];
    }, 60);
    return () => clearTimeout(launch);
  }, [pending, isUnlocked, inView, visible, blocked, reduced]);

  useEffect(() => {
    if (reduced || !visible || !inView) {
      stop();
      setPhase("idle");
    }
    if (reduced && pending) { remember(pending); setPending(null); }
  }, [reduced, visible, inView, pending]);

  return { ref, reduced, phase, count, pending: Boolean(pending), celebrating: phase !== "idle", running: inView && visible && !reduced };
};